/**
 * Thin client for the crowd-sourced land-data endpoints
 * (`/api/payment/crowd-data`). Auth is required by the backend, so every
 * mutating call carries the `x-auth-token` header the backend expects, the same
 * convention propertyActions uses (token is JSON-stringified in localStorage).
 *
 * The form layout is NOT hard-coded here. The crowd form shows the SAME
 * documents (and same price / SLA / fields) the standard document ORDER shows —
 * the full `states` catalogue for the chosen state+mode — plus an "Other"
 * free-text document. There is no separate crowd-data field/pricing config any
 * more (single source of truth):
 *   /static/crowd-data/index.json                → { fields: [STATE, MODE] } (which states/modes crowd supports)
 *   /static/states/<state>/<mode>/index.json     → the document catalogue (same list the order flow shows)
 *   /static/states/<state>/<mode>/<doc>/index.json          → search modes [{id,name}]
 *   /static/states/<state>/<mode>/<doc>/<form>/index.json   → { fields: [...] }
 *   /static/states/<state>/<mode>/<doc>/service.json        → pricing / services (price + SLA)
 * Crowd fields are OPTIONAL: the field schema's `required` drives the standard
 * order form, while the crowd form honours `crowdRequired` (all false) instead.
 */
import {
  AxiosPropertyInstance,
  AxiosPaymentInstance,
} from "../../common/utils/AxiosInstance";

// Crowd data is served by the PAYMENT service (/api/payment/crowd-data and
// /api/payment/crowd-data-request): uploads, requests, chat and the Cashfree
// payment links all live there now, alongside the credit charging they depend on.
const crowdApi = AxiosPaymentInstance;

// The form CONFIG is NOT part of that move — /static/crowd-data and
// /static/states are plain JSON served by `express.static("public")` on the
// property service, so those calls stay pointed at it.
const staticApi = AxiosPropertyInstance;

const BASE = "/crowd-data";
// "Request document" sibling collection, same flow as upload, no file, and a
// mandatory map location. Reuses the SAME form config (fetchCrowdForm/Mode).
const REQUEST_BASE = "/crowd-data-request";
const FORM_INDEX_URL = "/static/crowd-data/index.json";

function authHeader() {
  const token = JSON.parse(localStorage.getItem("token") || "null");
  return { "x-auth-token": token };
}

/** Fetch the top-level config: state options + modes. Auth-exempt. */
export async function fetchCrowdForm() {
  const res = await staticApi.get(FORM_INDEX_URL);
  return res.data;
}

// The free-text "Other" document (request a document not in the catalogue). The
// modal reveals a "Document name" input when this is picked.
export const OTHER_DOCUMENT = { id: "other", name: "Other" };

/**
 * Fetch the documents shown in the crowd form for a state+mode. This is the SAME
 * catalogue the standard document ORDER shows — the full `states` list — plus an
 * "Other" free-text document appended at the end. Returns `{ documents: [{ id,
 * name }] }`. Search modes / fields / pricing for each document come from `states`
 * (see the `fetchDoc*` helpers below). Auth-exempt.
 */
export async function fetchCrowdMode(state, mode) {
  const res = await staticApi.get(
    `/static/states/${state}/${mode}/index.json`
  );
  const catalogue = Array.isArray(res.data) ? res.data : [];
  const documents = catalogue
    .filter((d) => d && d.id)
    .map((d) => ({ id: d.id, name: d.name || d.id }));
  if (!documents.some((d) => d.id === OTHER_DOCUMENT.id)) {
    documents.push({ ...OTHER_DOCUMENT });
  }
  return { documents };
}

/* -------------------------------------------------------------------------- */
/* Shared `states` document config (search modes / fields / pricing)          */
/* -------------------------------------------------------------------------- */

/**
 * A document's search modes, from the shared states config:
 *   GET /static/states/<state>/<mode>/<doc>/index.json  → [{ id, name }, ...]
 * Returns [] when the document has no config (e.g. the "other" free-text doc).
 */
export async function fetchDocSearchModes(state, mode, docId) {
  try {
    const res = await staticApi.get(
      `/static/states/${state}/${mode}/${docId}/index.json`
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

/**
 * The field schema for one search mode, from the shared states config:
 *   GET /static/states/<state>/<mode>/<doc>/<form>/index.json → { fields: [...] }
 * The crowd form treats every field as OPTIONAL (it reads `crowdRequired`, which
 * the states JSON now carries as `false` on all fields). Returns [] on miss.
 */
export async function fetchDocFields(state, mode, docId, formId) {
  try {
    const res = await staticApi.get(
      `/static/states/${state}/${mode}/${docId}/${formId}/index.json`
    );
    return Array.isArray(res.data?.fields) ? res.data.fields : [];
  } catch {
    return [];
  }
}

/**
 * A document's service/pricing config, from the shared states config:
 *   GET /api/property/service-pricing/<state>/<mode>/<doc>
 * Shape: { <serviceTypeId>: { id, name, services: [{ id, name, payment, sla }] } }.
 * Returns null when the document has no pricing on file (e.g. "other").
 *
 * Prices moved out of the static service.json into the DB; this public endpoint
 * returns the SAME config shape the file did, so callers are unchanged. Served by
 * the property service, so it stays on `staticApi` (the property axios instance).
 */
export async function fetchDocService(state, mode, docId) {
  try {
    const res = await staticApi.get(`/service-pricing/${state}/${mode}/${docId}`);
    return res.data || null;
  } catch {
    return null;
  }
}

// Fallback price (credits) when a document has no resolvable credits service, or
// when the user requests a document without picking a delivery option (e.g. a
// year-dependent doc with no year entered). The BACKEND is authoritative
// (re-reads the same service.json + applies the same default), so this is only
// for display + the pre-submit balance check.
export const DOC_PRICE_FALLBACK = 99;

// A picked service is a PHYSICAL / hard-copy delivery (needs a shipping address)
// when it sits in the `hardCopy` serviceType group or its service id looks like a
// hard-copy option. Mirrors the standard document flow's hard-copy detection.
export function isHardCopyService(opt) {
  if (!opt) return false;
  const groupId = opt.serviceType?.id || "";
  const svcId = opt.service?.id || "";
  return groupId === "hardCopy" || /HARD_COPY/i.test(svcId);
}

// Remap a saved delivery address ({ ...fields, pinCode, _id, userId }) into the
// order `deliveryAddress` shape the backend stores: `pincode` (lowercased) and no
// DB-only keys. Mirrors the standard order flow's remap.
export function toDeliveryAddress(addr) {
  if (!addr || typeof addr !== "object") return null;
  const { _id, userId, __v, label, pinCode, ...rest } = addr;
  return { ...rest, pincode: addr.pincode || pinCode || "" };
}

/**
 * Flatten a service.json into the CREDITS service options the crowd request form
 * offers, each `{ serviceType: {id,name}, service: {id,name}, credits, sla, year }`.
 * Crowd requests are paid in credits, so cash-only services are skipped.
 *
 * `year` (when present in the config) is the registration-year range the option
 * is priced for, `{ start, end }` as numbers, or `null` when the option's price
 * does NOT depend on the year. The request form uses it to only reveal
 * year-dependent prices once the user has picked the registration year (the same
 * way the standard document order gates its delivery options by year).
 */
export function creditServiceOptions(serviceCfg) {
  if (!serviceCfg || typeof serviceCfg !== "object") return [];
  const out = [];
  Object.keys(serviceCfg).forEach((groupId) => {
    const group = serviceCfg[groupId];
    if (!group || !Array.isArray(group.services)) return;
    group.services.forEach((s) => {
      const amount = Number(s?.payment?.amount);
      if (s?.payment?.type === "CREDITS" && Number.isFinite(amount)) {
        const start = parseInt(s?.year?.start, 10);
        const end = parseInt(s?.year?.end, 10);
        const year =
          Number.isFinite(start) || Number.isFinite(end)
            ? {
                start: Number.isFinite(start) ? start : -Infinity,
                end: Number.isFinite(end) ? end : Infinity,
              }
            : null;
        out.push({
          serviceType: { id: groupId, name: group.name || groupId },
          service: { id: s.id, name: s.name || s.id },
          credits: amount,
          sla: s.sla || null,
          desc: s.desc || "",
          year,
        });
      }
    });
  });
  return out;
}

/**
 * The registration year (a number) the user has entered in the detail fields, or
 * null. Crowd forms carry it under the `YEAR` field id (a NUMBER field, so a
 * string like "2020"; a SELECT year would be `{ name, code }`). Used to gate
 * year-dependent delivery/pricing options.
 */
export function selectedYearFrom(selections) {
  const raw = selections?.YEAR;
  const v = raw && typeof raw === "object" ? raw.code ?? raw.name : raw;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Split credit service options into the ones that should be VISIBLE for a given
 * selected year and whether any option depends on the year at all. An option
 * with no `year` is always visible; a year-ranged option is visible only once a
 * matching year is picked. When a document has year-dependent pricing and no
 * year is chosen yet, `needsYear` is true and the caller shows "pick the year".
 */
export function visibleServicesForYear(options = [], selectedYear = null) {
  const hasYearPricing = options.some((o) => o && o.year);
  const visible = options.filter((o) => {
    if (!o) return false;
    if (!o.year) return true; // price independent of year → always
    if (!selectedYear) return false; // year-dependent but no year yet → hidden
    return selectedYear >= o.year.start && selectedYear <= o.year.end;
  });
  return {
    visible,
    hasYearPricing,
    needsYear: hasYearPricing && !selectedYear,
  };
}

// The crowd-data document pickers lead with the two most-requested documents
// and tuck the long tail (20-plus docs) behind a "More" expander.
export const FEATURED_DOC_IDS = ["sale-deed", "ec"];

/**
 * Split a state+mode `documents` list into `{ featured, rest }`. `featured`
 * holds the lead documents (Sale Deed, then EC) in that order, limited to the
 * ones actually present; `rest` keeps every other document in source order.
 */
export function splitFeaturedDocuments(documents = []) {
  const featured = FEATURED_DOC_IDS.map((id) =>
    documents.find((d) => d.id === id)
  ).filter(Boolean);
  const featuredIds = new Set(featured.map((d) => d.id));
  const rest = documents.filter((d) => !featuredIds.has(d.id));
  return { featured, rest };
}

/**
 * Build the multipart payload shared by create + update.
 *
 * Form fields are sent in the SAME shape order placement uses: `info` is an
 * object keyed by field id, each entry `{ id, name, value }` (value is the
 * picked option `{ name, code }` for SELECT fields, or a string for text). It
 * is JSON-stringified for transport, as is the `state` `{ id, name }` object.
 *
 * @param {object} fields - { info, state, landType, documentType,
 *   documentTypeName, address, lat, lng, polygon, private, verified }
 *   `polygon` is an array of { lat, lng } vertices (when the land was drawn as a
 *   polygon); `lat`/`lng` hold its centre, same fields a single pin uses.
 * @param {File|null} file - the single document
 */
function toFormData(fields = {}, file = null) {
  const fd = new FormData();
  // `file` may be a single File or an array of Files (multi-file upload). Every
  // file is appended under the same "file" key; the backend flattens them
  // (collectFiles) regardless of key, so 1..N documents all arrive together.
  const files = Array.isArray(file) ? file : file ? [file] : [];
  const scalars = ["landType", "documentType", "documentTypeName", "address"];
  scalars.forEach((k) => {
    if (fields[k] !== undefined && fields[k] !== null && fields[k] !== "") {
      fd.append(k, fields[k]);
    }
  });
  if (fields.info && Object.keys(fields.info).length > 0) {
    fd.append("info", JSON.stringify(fields.info));
  }
  if (fields.state && (fields.state.id || fields.state.name)) {
    fd.append("state", JSON.stringify(fields.state));
  }
  if (fields.verified !== undefined) fd.append("verified", fields.verified);
  if (fields.private !== undefined) fd.append("private", fields.private);
  // Explicit confirmation that the user accepted a credits charge (private
  // uploads beyond the free limit). The backend refuses to deduct without it.
  if (fields.confirmCharge !== undefined)
    fd.append("confirmCharge", fields.confirmCharge);
  if (
    fields.lat !== undefined &&
    fields.lat !== null &&
    fields.lng !== undefined &&
    fields.lng !== null
  ) {
    fd.append("lat", fields.lat);
    fd.append("lng", fields.lng);
  }
  if (Array.isArray(fields.polygon) && fields.polygon.length > 0) {
    fd.append("polygon", JSON.stringify(fields.polygon));
  }
  files.forEach((f) => fd.append("file", f));

  // Per-document upload: each document carries its OWN metadata (property type /
  // document type / detail fields) + its file. The metadata rides in a single
  // `documents` JSON array; each file is sent as `file<index>` so the backend can
  // pair them up. Used by the crowd upload flow; the request flow never sets it.
  if (Array.isArray(fields.documents) && fields.documents.length) {
    const meta = fields.documents.map((d) => ({
      state: d.state,
      landType: d.landType,
      documentType: d.documentType,
      documentTypeName: d.documentTypeName,
      info: d.info || {},
    }));
    fd.append("documents", JSON.stringify(meta));
    fields.documents.forEach((d, i) => {
      if (d.file) fd.append(`file${i}`, d.file);
    });
  }
  return fd;
}

/** Create a submission (one document, several documents, or a single .zip).
 *  `file` may be a single File or an array of Files. */
export async function createCrowdData(fields, file) {
  const res = await crowdApi.post(BASE, toFormData(fields, file), {
    headers: authHeader(),
  });
  return res.data;
}

/** Update fields of an existing submission and/or replace its document. */
export async function updateCrowdData(id, fields, file) {
  const res = await crowdApi.put(
    `${BASE}/${id}`,
    toFormData(fields, file),
    { headers: authHeader() }
  );
  return res.data;
}

/** Quote the credits cost of keeping `count` uploads private (create) or making
 *  one upload private (flip, pass its id as `excludeId`). Returns
 *  { willCharge, cost, chargedCount, balance, enough, freeLimit, price, currentCount }.
 *  Used to CONFIRM the charge before the action. */
export async function getPrivateUploadQuote(count = 1, excludeId = null) {
  const params = { count };
  if (excludeId) params.excludeId = excludeId;
  const res = await crowdApi.get(`${BASE}/private-quote`, {
    headers: authHeader(),
    params,
  });
  return res.data?.data?.item || null;
}

/** Flip an upload's visibility (public ↔ private), owner only. Returns the
 *  updated record. A private upload is hidden from the public map/list. */
export async function setCrowdVisibility(id, isPrivate, confirmCharge = false) {
  const res = await crowdApi.patch(
    `${BASE}/${id}/visibility`,
    // `confirmCharge` accepts a credits charge when making private beyond the
    // free limit; the backend refuses to deduct without it.
    { private: isPrivate, confirmCharge },
    { headers: authHeader() }
  );
  return res.data?.data?.item || null;
}

/** Delete a submission (archived server-side into `archive_crowd_datas`). */
export async function deleteCrowdData(id) {
  const res = await crowdApi.delete(`${BASE}/${id}`, {
    headers: authHeader(),
  });
  return res.data;
}

/** List submissions with optional filters/pagination. */
export async function listCrowdData(params = {}) {
  const res = await crowdApi.get(BASE, {
    headers: authHeader(),
    params,
  });
  return res.data;
}

/**
 * Map points, all submissions that have a location, as flat
 * { id, lat, lng, ... } objects. Optional filters: { landType, state, verified }.
 */
export async function fetchCrowdLocations(params = {}) {
  const res = await crowdApi.get(`${BASE}/locations`, {
    headers: authHeader(),
    params,
  });
  return res.data?.data?.items || [];
}

/**
 * The signed-in contributor's upload counts: { total, verified, pending }.
 */
export async function fetchMyStats() {
  const res = await crowdApi.get(`${BASE}/my/stats`, {
    headers: authHeader(),
  });
  return (
    res.data?.data?.item || { total: 0, verified: 0, pending: 0, rejected: 0 }
  );
}

/** The signed-in contributor's own submissions (most recent first). */
export async function fetchMyUploads(params = { limit: 50 }) {
  const res = await crowdApi.get(`${BASE}/my`, {
    headers: authHeader(),
    params,
  });
  return res.data?.data?.items || [];
}

/** Full detail for one submission (GET /crowd-data/:id → { data: { item } }). */
export async function fetchCrowdData(id) {
  const res = await crowdApi.get(`${BASE}/${id}`, {
    headers: authHeader(),
  });
  return res.data?.data?.item || null;
}

/* -------------------------------------------------------------------------- */
/* Request-document endpoints (/crowd-data-request)                           */
/* -------------------------------------------------------------------------- */

/**
 * Build the JSON body for a request submission. Unlike the upload flow there is
 * NO file, so this is plain JSON (axios serialises it): `info` and `state` stay
 * as objects, `polygon` as an array of { lat, lng } vertices, and `lat`/`lng`
 * carry the centre point. A map location (point or polygon) is required by the
 * backend.
 *
 * @param {object} fields - { info, state, landType, documentType,
 *   documentTypeName, address, lat, lng, polygon }
 */
function toRequestBody(fields = {}) {
  const body = {};
  // `sourceOrderId` = the contributor's upload order id when a request is started
  // from their map upload; the backend uses it to auto-deliver + reward 20%.
  // `otpRequestId` / `ecWithoutOtp`: an instant EC from the map card — fetched
  // with the customer's OTP, or placed without it (see MapRequestCard).
  [
    "landType",
    "documentType",
    "documentTypeName",
    "address",
    "sourceOrderId",
    "otpRequestId",
    "ecWithoutOtp",
  ].forEach(
    (k) => {
      if (fields[k] !== undefined && fields[k] !== null && fields[k] !== "") {
        body[k] = fields[k];
      }
    }
  );
  if (fields.info && Object.keys(fields.info).length > 0) body.info = fields.info;
  if (fields.state && (fields.state.id || fields.state.name)) {
    body.state = fields.state;
  }
  // The picked service (from the document's states service.json) drives pricing.
  if (fields.serviceType && fields.serviceType.id) {
    body.serviceType = fields.serviceType;
  }
  if (fields.service && fields.service.id) body.service = fields.service;
  // Hard-copy delivery address (only present when a physical/hardCopy service was
  // picked). Forwarded as-is; the backend persists it on the order.
  if (fields.deliveryAddress && typeof fields.deliveryAddress === "object") {
    body.deliveryAddress = fields.deliveryAddress;
  }
  if (
    fields.lat !== undefined &&
    fields.lat !== null &&
    fields.lng !== undefined &&
    fields.lng !== null
  ) {
    body.lat = fields.lat;
    body.lng = fields.lng;
  }
  if (Array.isArray(fields.polygon) && fields.polygon.length > 0) {
    body.polygon = fields.polygon;
  }
  // The revenue hierarchy resolved for the pin ({ district, mandal, village,
  // city, pincode }, any part optional). The backend stores it on the request
  // AND mirrors it into the order payload, so it shows on the customer's order
  // and in the admin order drawer.
  if (
    fields.locationDetails &&
    typeof fields.locationDetails === "object" &&
    Object.keys(fields.locationDetails).length > 0
  ) {
    body.locationDetails = fields.locationDetails;
  }
  return body;
}

/**
 * Create a document request (map location is mandatory; no file). With
 * `payShortfall`, a short wallet comes back as a Cashfree checkout
 * ({ sessionId, orderId, ... }) instead of an "insufficient credits" error.
 */
export async function createCrowdDataRequest(fields, { payShortfall } = {}) {
  const body = toRequestBody(fields);
  if (payShortfall) body.payShortfall = true;
  const res = await crowdApi.post(REQUEST_BASE, body, {
    headers: authHeader(),
  });
  return res.data;
}

/** Update fields of an existing request (owner). */
export async function updateCrowdDataRequest(id, fields) {
  const res = await crowdApi.put(
    `${REQUEST_BASE}/${id}`,
    toRequestBody(fields),
    { headers: authHeader() }
  );
  return res.data;
}

/** Delete a request (archived server-side into `archive_crowd_data_requests`). */
export async function deleteCrowdDataRequest(id) {
  const res = await crowdApi.delete(`${REQUEST_BASE}/${id}`, {
    headers: authHeader(),
  });
  return res.data;
}

/**
 * Map points, all requests that have a location, as flat { id, lat, lng, ... }
 * objects. Optional filters: { landType, state, status }.
 */
export async function fetchCrowdRequestLocations(params = {}) {
  const res = await crowdApi.get(`${REQUEST_BASE}/locations`, {
    headers: authHeader(),
    params,
  });
  return res.data?.data?.items || [];
}

/** The signed-in contributor's request counts: { total, completed, pending }. */
export async function fetchMyRequestStats() {
  const res = await crowdApi.get(`${REQUEST_BASE}/my/stats`, {
    headers: authHeader(),
  });
  return (
    res.data?.data?.item || { total: 0, completed: 0, pending: 0, rejected: 0 }
  );
}

/** The signed-in contributor's own requests (most recent first). */
export async function fetchMyRequests(params = { limit: 50 }) {
  const res = await crowdApi.get(`${REQUEST_BASE}/my`, {
    headers: authHeader(),
    params,
  });
  return res.data?.data?.items || [];
}

/** Full detail for one request (GET /crowd-data-request/:id → { data: { item } }). */
export async function fetchCrowdDataRequest(id) {
  const res = await crowdApi.get(`${REQUEST_BASE}/${id}`, {
    headers: authHeader(),
  });
  return res.data?.data?.item || null;
}

/* -------------------------------------------------------------------------- */
/* Upload ACCESS (permission to contribute documents)                         */
/* -------------------------------------------------------------------------- */

// Not every customer may upload. `canUpload` gates the full upload flow; when
// false the UI shows only Requests plus a "Share Documents" CTA that submits an
// access request an admin then approves.

/**
 * The signed-in customer's upload-access state.
 * GET /crowd-data/upload-permission/my → { canUpload, status, request }
 * `status` is NONE | CREATED | APPROVED | REJECTED. Falls back to a locked,
 * no-request state on any error so the UI degrades to "request access".
 */
export async function fetchMyUploadAccess() {
  try {
    const res = await crowdApi.get(`${BASE}/upload-permission/my`, {
      headers: authHeader(),
    });
    return (
      res.data?.data?.item || {
        canUpload: false,
        status: "NONE",
        request: null,
      }
    );
  } catch {
    return { canUpload: false, status: "NONE", request: null };
  }
}

/**
 * Request permission to contribute documents (idempotent — one per customer).
 * POST /crowd-data/upload-permission { message? } → the access record.
 */
export async function requestUploadAccess(message) {
  const res = await crowdApi.post(
    `${BASE}/upload-permission`,
    message ? { message } : {},
    { headers: authHeader() }
  );
  return res.data?.data?.item || null;
}

/* -------------------------------------------------------------------------- */
/* Chat (contributor ↔ admin), for both uploads and requests                 */
/* -------------------------------------------------------------------------- */

// The customer chat routes are owner-scoped server-side, so a contributor can
// only chat on their OWN submission / request. `kind` picks the collection.
function chatBase(kind) {
  return kind === "request" ? REQUEST_BASE : BASE;
}

/** Send a text message on your submission/request. Returns the updated record. */
export async function sendCrowdMessage(kind, id, msg) {
  const res = await crowdApi.put(
    `${chatBase(kind)}/${id}/chat/send-msg`,
    { msg },
    { headers: authHeader() }
  );
  return res.data?.data?.item || null;
}

/** Send a file attachment on your submission/request chat. */
export async function uploadCrowdMessage(kind, id, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await crowdApi.put(
    `${chatBase(kind)}/${id}/chat/upload-doc`,
    fd,
    { headers: authHeader() }
  );
  return res.data?.data?.item || null;
}

/** Mark the admin's messages as read on your submission/request. */
export async function readCrowdMessages(kind, id) {
  const res = await crowdApi.put(
    `${chatBase(kind)}/${id}/chat/read-msg`,
    {},
    { headers: authHeader() }
  );
  return res.data?.data?.item || null;
}

/**
 * List the Cashfree payment links raised on your OWN request (owner-scoped
 * server-side). Each carries a `paymentURL` the requester opens to pay.
 * GET /crowd-data-request/:id/payment-link → { data: { item: { paymentLinks } } }
 */
export async function fetchRequestPaymentLinks(id) {
  const res = await crowdApi.get(
    `${REQUEST_BASE}/${id}/payment-link`,
    { headers: authHeader() }
  );
  return res.data?.data?.item?.paymentLinks || [];
}
